import Swal from "sweetalert2";
import {
  Col,
  Form,
  Label,
  ModalBody,
  Row,
} from "reactstrap";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState, Fragment } from "react";
import { Btn, H6 } from "../../../../../AbstractElements";
import Dropzone from "react-dropzone";
import SvgIcon from "../../../../CommonComponents/SvgIcon/SvgIcon";
import { Upload } from "react-feather";

//SERVICES
import {
  applicantsService,
} from "../../../../../Services/applicantsService";

const INITIAL_STATE = {
  profile_url: "applicatns-profile/default.png",
};

interface UploadFormProps {
  toggle?: () => void;
  ApplNo: string;
}

const UploadProfileForm: React.FC<UploadFormProps> = ({ toggle, ApplNo }) => {
  const queryClient = useQueryClient();
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const onDrop = (acceptedFiles: File[]) =>
    setUploadedFiles((prevFiles) => [...prevFiles, ...acceptedFiles]);
  const removeFile = (indexToRemove: number) =>
    setUploadedFiles((prevFiles) =>
      prevFiles.filter((_, index) => index !== indexToRemove),
    );

  const [formData, setFormData] = useState({
    profile_url: "",
  });

  const uploadProfileMutation = useMutation<
    any,
    any, // error type
    FormData // input type for .mutate()
  >({
    mutationFn: (formData: FormData) =>
      applicantsService.uploadProfile(ApplNo, formData), // pass FormData
    onSuccess: (result) => {
      queryClient.invalidateQueries();
      setFormData(INITIAL_STATE);
      if (toggle) {
        toggle();
      }
      Swal.close();
      Swal.fire({
        allowOutsideClick: false,
        allowEscapeKey: false,
        icon: "success",
        title: "Transaction Successful",
        text: "Applicant Profile has been successfully updated.",
        confirmButtonColor: "#4e73df",
      });
    },
    onError: (error: any) => {
      const responseData = error.response?.data;

      let errorMessage = "Failed to update applicant picture. Please check your inputs.";

      if (responseData?.errors) {
        const firstErrorKey = Object.keys(responseData.errors)[0];
        errorMessage = responseData.errors[firstErrorKey][0];
      } else if (responseData?.message) {
        errorMessage = responseData.message;
      }

      Swal.close();
      Swal.fire({
        icon: "error",
        title: "Transaction Failed",
        text: errorMessage,
        confirmButtonColor: "#4e73df",
      });
    },
  });

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const data = new FormData();

    if (uploadedFiles.length > 0) {
      Swal.fire({
        title: "Are you sure?",
        text: "Do you want to upload this image?",
        icon: "question",
        showCancelButton: true,
  
        confirmButtonText: '<i class="fa fa-upload"></i> Upload Image',
        cancelButtonText: '<i class="fa fa-times"></i> Cancel',
  
        confirmButtonColor: "#4e73df", // Primary
        cancelButtonColor: "#dc3545",  // Danger
        allowOutsideClick: false,
        allowEscapeKey: false,
  
      }).then((result) => {
        if (result.isConfirmed) {
          data.append("profile_url", uploadedFiles[0]);
          data.append("_method", "PATCH");

          uploadProfileMutation.mutate(data);
          Swal.fire({
            title: "Uploading...",
            text: "Please wait while your profile photo is being uploaded.",
            allowOutsideClick: false,
            allowEscapeKey: false,
            didOpen: () => {
              Swal.showLoading();
            },
          });
        }
      });
    } else {
      Swal.fire({
        allowOutsideClick: false,
        allowEscapeKey: false,
        icon: "error",
        title: "Error",
        text: "Please select a photo to upload.",
        confirmButtonColor: "#4e73df",
        
      });
    }

    
  };

  return (
    <ModalBody>
      <Form className="needs-validation" onSubmit={handleSubmit}>
        <Row className="g-3">
          <Col md="12">
            <Label>
              Applicant Picture<b className="text-danger">*</b>
            </Label>
            <Fragment>
              <Dropzone
                onDrop={onDrop}
                maxFiles={1} // allow only one file
                accept={{
                  "image/jpeg": [".jpeg", ".jpg"],
                  "image/png": [".png"],
                }}
              >
                
                {({ getRootProps, getInputProps }) => (
                  <div
                    {...getRootProps()}
                    style={{
                      border: "2px dashed #d6d6d6",
                      borderRadius: 10,
                      padding: 30,
                      textAlign: "center",
                      cursor: "pointer",
                    }}
                  >
                    <input {...getInputProps()} />
                    {uploadedFiles.length === 0 ? (
                      <div className="dz-message needsclick">
                        <h5>📁 Drag & Drop your file here</h5>
                        <p className="text-muted">
                          or <strong>Click to Browse</strong>
                        </p>

                        <small>
                          JPG, JPEG, PNG
                        </small>
                      </div>
                    ) : (
                      <div className="uploaded-files">
                        {uploadedFiles.map((file, index) => (
                          <div key={index} className="file-card">
                            {file.type.startsWith("image/") ? (
                              <img
                                src={URL.createObjectURL(file)}
                                alt={file.name}
                                className="file-thumbnail"
                              />
                            ) : (
                              <div className="file-placeholder">
                                {file.name.split(".").pop()?.toUpperCase()} File
                              </div>
                            )}
                            <p className="file-name">{file.name}</p>
                            <p className="file-size">
                              {(file.size / 1024).toFixed(2)} KB
                            </p>
                            <button
                              onClick={() => removeFile(index)}
                              className="remove-button"
                              title="Remove file"
                            >
                              ×
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </Dropzone>
            </Fragment>
          </Col>
          <Col md="12 mt-3">
            <Btn
              color="primary"
              className="w-100"
              type="submit"
              disabled={uploadProfileMutation.isPending}
            >
              <Upload size={16} className="me-1"/>
              {uploadProfileMutation.isPending ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm me-2"
                    role="status"
                    aria-hidden="true"
                  ></span>
                    Uploading Applicants Picture, Please Wait...
                  </>
                  ) : (
                    "Upload Picture"
                  )}
            </Btn>
          </Col>
        </Row>
      </Form>
    </ModalBody>
    
  );
};

export default UploadProfileForm;
